import React, { useState, useEffect, useCallback } from 'react'
import { useFetch, useToast } from '../../hooks'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Paper,
  Divider,
  Tooltip,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  RotateLeft as ResetIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import { ResponsiveTable } from '../../components/TableComponents'
import { LoadingSpinner, StatusChip, ToastNotification } from '../../components/atoms'
import { AutoCompleteInput } from '@components/AutoCompleteInput'
import QACriteriaDialog from './SettingsQAConfig/QACriteriaDialog'
import CriteriaHistoryDialog from './SettingsQAConfig/CriteriaHistoryDialog'
import sharedStyles from './qualityAssurance/SettingsQA.module.scss'
import styles from './SettingsQAConfig.module.scss'

export default function SettingsQAConfig() {
  const [saving, setSaving] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [selectedCampaignsForCreate, setSelectedCampaignsForCreate] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCriterion, setEditingCriterion] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingCriterion, setDeletingCriterion] = useState(null)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [historyData, setHistoryData] = useState([])
  const [testCallId, setTestCallId] = useState('')
  const [testResult, setTestResult] = useState(null)
  const [testLoading, setTestLoading] = useState(false)
  const { toast, showToast, closeToast } = useToast()
  const [campaignCalls, setCampaignCalls] = useState([])
  const [callsLoading, setCallsLoading] = useState(false)
  const [callInputMode, setCallInputMode] = useState('dropdown')
  const [manualCallId, setManualCallId] = useState('')
  const [callValidation, setCallValidation] = useState(null)
  const [validating, setValidating] = useState(false)

  const [formData, setFormData] = useState({
    criterionName: '',
    criterionType: 'verification',
    evaluationConfig: {
      checkItems: [],
      passCriteria: 'any_2',
      naCondition: '',
    },
    evaluationPrompt: '',
    isActive: true,
  })
  const [generatedPrompt, setGeneratedPrompt] = useState('')
  const [promptLoading, setPromptLoading] = useState(false)

  const [baseCriteriaConfig, setBaseCriteriaConfig] = useState([])
  const [baseCriteriaLoading, setBaseCriteriaLoading] = useState(false)
  const [campaignsWithOverrides, setCampaignsWithOverrides] = useState([])
  const [savingBaseCriteria, setSavingBaseCriteria] = useState(false)
  const [filterOptions, setFilterOptions] = useState({ campaigns: [], dispositions: [] })
  const [baseCriteriaHasChanges, setBaseCriteriaHasChanges] = useState(false)
  const [resetConfirmDialog, setResetConfirmDialog] = useState(false)
  const handleCampaignChange = useCallback((event, newValue) => setSelectedCampaign(newValue), [])
  const handleResetDialogClose = useCallback(() => setResetConfirmDialog(false), [])

  // Prompt config state (for Base Criteria Prompt Rules section)
  const [allBaseCriteria, setAllBaseCriteria] = useState([])
  const [allBaseCriteriaLoading, setAllBaseCriteriaLoading] = useState(false)
  const [promptConfigChanges, setPromptConfigChanges] = useState({})
  const [savingPromptConfig, setSavingPromptConfig] = useState(null)
  const [promptTestCallIds, setPromptTestCallIds] = useState({})
  const [promptTestResults, setPromptTestResults] = useState({})
  const [promptTestLoading, setPromptTestLoading] = useState({})

  const { data: evaluationTemplates } = useFetch(apiEndpoints.qaConfigTemplates, {
    initialData: {},
    transform: (data) => data.data || {},
    errorMessage: 'Error fetching evaluation templates',
  })

  const { data: campaignsWithCriteria, refetch: refetchCampaignsWithCriteria } = useFetch(
    apiEndpoints.qaConfigCampaigns,
    {
      initialData: [],
      transform: (data) => data.data || [],
      errorMessage: 'Error fetching campaigns with criteria',
    },
  )

  const { data: allCampaigns } = useFetch(apiEndpoints.campaigns, {
    initialData: [],
    transform: (data) => {
      const campaigns = Array.isArray(data) ? data : data.data || []
      return campaigns.map((c) => ({
        name: c.original_name || c.campaign_name || c.name,
        displayName: c.campaign_name || c.original_name || c.name,
      }))
    },
    errorMessage: 'Error fetching all campaigns',
  })

  const {
    data: criteria,
    loading: criteriaLoading,
    refetch: refetchCriteria,
  } = useFetch(
    `${apiEndpoints.qaConfigCriteria}?campaign=${encodeURIComponent(selectedCampaign?.name || '')}&includeInactive=true`,
    {
      initialData: [],
      skip: !selectedCampaign,
      dependencies: [selectedCampaign?.name],
      transform: (data) => data.data || [],
      errorMessage: 'Failed to fetch criteria',
    },
  )

  const generatePromptPreview = useCallback(async (criterionType, config) => {
    /* istanbul ignore next -- custom type exits early */
    if (criterionType === 'custom') {
      setGeneratedPrompt('')
      return
    }

    if (!config.checkItems || config.checkItems.length === 0) {
      setGeneratedPrompt('')
      return
    }

    setPromptLoading(true)
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigGeneratePrompt, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          criterionType,
          evaluationConfig: config,
        }),
      })
      const data = await response.json()
      setGeneratedPrompt(data.data?.prompt || '')
    } catch (err) {
      console.error('Error generating prompt preview:', err)
      setGeneratedPrompt('')
    } finally {
      setPromptLoading(false)
    }
  }, [])

  const fetchCampaignsWithOverrides = useCallback(async () => {
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigBaseCriteriaCampaigns)
      const data = await response.json()
      setCampaignsWithOverrides(data.data || [])
    } catch (err) {
      console.error('Error fetching campaigns with overrides:', err)
    }
  }, [])

  const fetchBaseCriteriaConfig = useCallback(async (campaignName) => {
    /* istanbul ignore next -- campaign always provided when called */
    if (!campaignName) {
      setBaseCriteriaConfig([])
      return
    }
    setBaseCriteriaLoading(true)
    try {
      const response = await authenticatedFetch(
        apiEndpoints.qaConfigBaseCriteriaCampaign(campaignName),
      )
      const data = await response.json()
      setBaseCriteriaConfig(data.data || [])
      setBaseCriteriaHasChanges(false)
    } catch (err) {
      console.error('Error fetching base criteria config:', err)
    } finally {
      setBaseCriteriaLoading(false)
    }
  }, [])

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await authenticatedFetch(apiEndpoints.autoQaFilters)
      const data = await response.json()
      setFilterOptions({
        campaigns: data.data?.campaigns || [],
        dispositions: data.data?.dispositions || [],
      })
    } catch (err) {
      console.error('Error fetching filter options:', err)
    }
  }, [])

  useEffect(() => {
    fetchCampaignsWithOverrides()
    fetchFilterOptions()
  }, [fetchCampaignsWithOverrides, fetchFilterOptions])

  const evaluationConfigJson = JSON.stringify(formData.evaluationConfig)
  useEffect(() => {
    if (dialogOpen && formData.criterionType !== 'custom') {
      generatePromptPreview(formData.criterionType, formData.evaluationConfig)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, formData.criterionType, evaluationConfigJson, generatePromptPreview])

  useEffect(() => {
    if (selectedCampaign) {
      fetchBaseCriteriaConfig(selectedCampaign.name)
    } else {
      setBaseCriteriaConfig([])
      setBaseCriteriaHasChanges(false)
    }
  }, [selectedCampaign, fetchBaseCriteriaConfig])

  const handleOpenDialog = (criterion = null) => {
    if (criterion) {
      setEditingCriterion(criterion)
      const isLegacyCustom = !criterion.criterionType || criterion.criterionType === 'custom'
      setFormData({
        criterionName: criterion.criterionName,
        criterionType: criterion.criterionType || 'custom',
        evaluationConfig: criterion.evaluationConfig || {
          checkItems: [],
          passCriteria: 'any_2',
          naCondition: '',
        },
        evaluationPrompt: criterion.evaluationPrompt || '',
        isActive: criterion.isActive,
      })
      if (!isLegacyCustom && criterion.evaluationPrompt) {
        setGeneratedPrompt(criterion.evaluationPrompt)
      }
    } else {
      setEditingCriterion(null)
      const defaultType = Object.keys(evaluationTemplates)[0] || 'verification'
      const defaultTemplate = evaluationTemplates[defaultType]
      setFormData({
        criterionName: '',
        criterionType: defaultType,
        evaluationConfig: {
          checkItems: [],
          passCriteria: defaultTemplate?.defaultPassCriteria || 'any_2',
          naCondition: '',
        },
        evaluationPrompt: '',
        isActive: true,
      })
      setGeneratedPrompt('')
    }
    setTestResult(null)
    setTestCallId('')
    setManualCallId('')
    setCallValidation(null)
    setCallInputMode('dropdown')
    setCampaignCalls([])
    setSelectedCampaignsForCreate(selectedCampaign ? [selectedCampaign] : [])
    setDialogOpen(true)
    if (selectedCampaign) {
      fetchCampaignCalls()
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingCriterion(null)
    setFormData({
      criterionName: '',
      criterionType: 'verification',
      evaluationConfig: {
        checkItems: [],
        passCriteria: 'any_2',
        naCondition: '',
      },
      evaluationPrompt: '',
      isActive: true,
    })
    setGeneratedPrompt('')
    setTestResult(null)
    setTestCallId('')
  }

  const handleSaveCriterion = async () => {
    /* istanbul ignore next -- validation guards behind disabled save button */
    if (!formData.criterionName.trim()) {
      showToast('Criterion name is required', 'error')
      return
    }

    /* istanbul ignore next -- validation guards behind disabled save button */
    if (formData.criterionType === 'custom') {
      if (!formData.evaluationPrompt.trim()) {
        showToast('Evaluation prompt is required for custom criteria', 'error')
        return
      }
    } else {
      if (
        !formData.evaluationConfig.checkItems ||
        formData.evaluationConfig.checkItems.length === 0
      ) {
        showToast('Please select at least one check item', 'error')
        return
      }
    }

    setSaving(true)

    try {
      const userEmail = localStorage.getItem('userEmail') || 'admin'

      if (editingCriterion) {
        const updatePayload = {
          criterionName: formData.criterionName,
          criterionType: formData.criterionType,
          evaluationConfig: formData.criterionType === 'custom' ? {} : formData.evaluationConfig,
          evaluationPrompt:
            formData.criterionType === 'custom' ? formData.evaluationPrompt : generatedPrompt,
          isActive: formData.isActive,
          modifiedBy: userEmail,
        }

        const response = await authenticatedFetch(
          apiEndpoints.qaConfigCriterion(editingCriterion.id),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload),
          },
        )

        if (!response.ok) {
          throw new Error('Failed to update criterion')
        }

        showToast('Criterion updated successfully')
      } else {
        const campaignsToCreate =
          selectedCampaignsForCreate.length > 0
            ? selectedCampaignsForCreate
            : selectedCampaign
              ? [selectedCampaign]
              : []

        /* istanbul ignore next -- campaign always selected via UI */
        if (campaignsToCreate.length === 0) {
          showToast('Please select at least one campaign', 'error')
          return
        }

        const createPromises = campaignsToCreate.map(async (campaign) => {
          const response = await authenticatedFetch(apiEndpoints.qaConfigCriteria, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              campaignName: campaign.name,
              criterionName: formData.criterionName,
              criterionType: formData.criterionType,
              evaluationConfig:
                formData.criterionType === 'custom' ? {} : formData.evaluationConfig,
              evaluationPrompt:
                formData.criterionType === 'custom' ? formData.evaluationPrompt : null,
              createdBy: userEmail,
            }),
          })
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error || `Failed to create criterion for ${campaign.name}`)
          }
          return response
        })

        const results = await Promise.allSettled(createPromises)
        const failures = results.filter((r) => r.status === 'rejected')
        const successes = results.filter((r) => r.status === 'fulfilled')

        /* istanbul ignore next -- partial batch failure path */
        if (failures.length > 0 && successes.length > 0) {
          showToast(
            `Created ${successes.length} criterion(s), ${failures.length} failed`,
            'warning',
          )
        } else if (failures.length > 0) {
          throw new Error(failures[0].reason?.message || 'Failed to create criteria')
        } else {
          showToast(`Criterion created for ${campaignsToCreate.length} campaign(s)`)
        }
      }

      handleCloseDialog()
      refetchCriteria()
      refetchCampaignsWithCriteria()
    } catch (err) {
      console.error('Error saving criterion:', err)
      showToast('Failed to save criterion', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (criterion) => {
    setDeletingCriterion(criterion)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingCriterion) return

    setSaving(true)
    try {
      const userEmail = localStorage.getItem('userEmail') || 'admin'

      const response = await authenticatedFetch(
        apiEndpoints.qaConfigCriterion(deletingCriterion.id),
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deletedBy: userEmail }),
        },
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete criterion')
      }

      showToast('Criterion deleted successfully')
      setDeleteDialogOpen(false)
      setDeletingCriterion(null)
      refetchCriteria()
      refetchCampaignsWithCriteria()
    } catch (err) {
      console.error('Error deleting criterion:', err)
      showToast('Failed to delete criterion', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleViewHistory = async (criterion) => {
    setSaving(true)
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigCriterionHistory(criterion.id))
      const data = await response.json()
      setHistoryData(data.data || [])
      setHistoryDialogOpen(true)
    } catch (err) {
      console.error('Error fetching history:', err)
      showToast('Failed to fetch history', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleTestPrompt = async () => {
    const promptToTest =
      formData.criterionType === 'custom' ? formData.evaluationPrompt : generatedPrompt

    /* istanbul ignore next -- guarded by disabled test button */
    if (!promptToTest.trim() || !testCallId.trim()) {
      showToast('Prompt and test call ID are required', 'error')
      return
    }

    setTestLoading(true)
    setTestResult(null)

    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigCriteriaTest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptToTest,
          callId: testCallId,
        }),
      })
      const data = await response.json()
      setTestResult(data.data)
    } catch (err) {
      console.error('Error testing prompt:', err)
      setTestResult({ success: false, error: 'Failed to test prompt' })
    } finally {
      setTestLoading(false)
    }
  }

  const insertVariable = (variable) => {
    setFormData((prev) => ({
      ...prev,
      evaluationPrompt: prev.evaluationPrompt + variable,
    }))
  }

  const toggleCheckItem = (itemKey) => {
    setFormData((prev) => {
      const currentItems = prev.evaluationConfig.checkItems || []
      const newItems = currentItems.includes(itemKey)
        ? currentItems.filter((k) => k !== itemKey)
        : [...currentItems, itemKey]

      return {
        ...prev,
        evaluationConfig: {
          ...prev.evaluationConfig,
          checkItems: newItems,
        },
      }
    })
  }

  const handleTypeChange = (newType) => {
    const template = evaluationTemplates[newType]
    setFormData((prev) => ({
      ...prev,
      criterionType: newType,
      evaluationConfig: {
        checkItems: [],
        passCriteria: template?.defaultPassCriteria || 'any_2',
        naCondition: '',
      },
    }))
    setGeneratedPrompt('')
  }

  const getCurrentTemplate = () => {
    return evaluationTemplates[formData.criterionType] || null
  }

  const fetchCampaignCalls = useCallback(async () => {
    if (!selectedCampaign) return

    setCallsLoading(true)
    try {
      const response = await authenticatedFetch(
        `${apiEndpoints.qaConfigCalls}?campaign=${encodeURIComponent(selectedCampaign.name)}&limit=50`,
      )
      const data = await response.json()
      setCampaignCalls(data.data || [])
      /* istanbul ignore next -- API error fallback */
    } catch (err) {
      console.error('Error fetching campaign calls:', err)
      setCampaignCalls([])
    } finally {
      setCallsLoading(false)
    }
  }, [selectedCampaign])

  const verifyCallId = async (callId) => {
    if (!callId.trim() || !selectedCampaign) {
      setCallValidation(null)
      return
    }

    setValidating(true)
    try {
      const response = await authenticatedFetch(
        `${apiEndpoints.qaConfigCallsVerify}?callId=${encodeURIComponent(callId)}&campaign=${encodeURIComponent(selectedCampaign.name)}`,
      )
      const data = await response.json()
      setCallValidation(data.data)
      if (data.data?.valid) {
        setTestCallId(callId)
      }
    } catch (err) {
      console.error('Error verifying call:', err)
      setCallValidation({ valid: false, reason: 'Verification failed' })
    } finally {
      setValidating(false)
    }
  }

  const insertSamplePrompt = () => {
    const samplePrompt = `Evaluate whether the agent properly verified the caller's identity before discussing sensitive information.

Check if the agent:
1. Asked for the caller's full name
2. Confirmed account or case number
3. Verified at least one additional piece of identifying information (e.g., date of birth, address, phone number)

Use the following transcript for evaluation:
{{transcript}}

Call Notes: {{callNotes}}

If the call does not involve sensitive information (e.g., general inquiry), mark as N_A.

Respond with exactly one of: PASS, FAIL, or N_A followed by a brief reason.`

    setFormData((prev) => ({
      ...prev,
      criterionType: 'custom',
      evaluationPrompt: samplePrompt,
    }))
  }

  const handleToggleBaseCriterion = (criterionKey) => {
    setBaseCriteriaConfig((prev) =>
      prev.map((c) =>
        c.criterionKey === criterionKey ? { ...c, isEnabled: !c.isEnabled, isOverridden: true } : c,
      ),
    )
    setBaseCriteriaHasChanges(true)
  }

  const handleBaseDispositionChange = (criterionKey, newDispositions) => {
    setBaseCriteriaConfig((prev) =>
      prev.map((c) =>
        c.criterionKey === criterionKey
          ? { ...c, appliesToDispositions: newDispositions, isOverridden: true }
          : c,
      ),
    )
    setBaseCriteriaHasChanges(true)
  }

  const handleBaseFilterModeChange = (criterionKey, mode) => {
    setBaseCriteriaConfig((prev) =>
      prev.map((c) =>
        c.criterionKey === criterionKey
          ? { ...c, dispositionFilterMode: mode, appliesToDispositions: [], isOverridden: true }
          : c,
      ),
    )
    setBaseCriteriaHasChanges(true)
  }

  const handleSaveBaseCriteria = async () => {
    if (!selectedCampaign) return
    setSavingBaseCriteria(true)
    try {
      const overrides = baseCriteriaConfig
        .filter((c) => c.isOverridden)
        .map((c) => ({
          criterionKey: c.criterionKey,
          isEnabled: c.isEnabled,
          appliesToDispositions: c.appliesToDispositions || [],
          dispositionFilterMode: c.dispositionFilterMode || 'include',
        }))

      await authenticatedFetch(apiEndpoints.qaConfigBaseCriteriaCampaign(selectedCampaign.name), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }),
      })
      setBaseCriteriaHasChanges(false)
      showToast('Base criteria configuration saved')
      fetchCampaignsWithOverrides()
      fetchBaseCriteriaConfig(selectedCampaign.name)
    } catch (err) {
      console.error('Error saving base criteria config:', err)
      showToast('Failed to save base criteria configuration', 'error')
    } finally {
      setSavingBaseCriteria(false)
    }
  }

  const handleResetBaseCriteria = async () => {
    if (!selectedCampaign) return
    try {
      await authenticatedFetch(apiEndpoints.qaConfigBaseCriteriaCampaign(selectedCampaign.name), {
        method: 'DELETE',
      })
      setResetConfirmDialog(false)
      setBaseCriteriaHasChanges(false)
      showToast('Base criteria reset to defaults')
      fetchCampaignsWithOverrides()
      fetchBaseCriteriaConfig(selectedCampaign.name)
    } catch (err) {
      console.error('Error resetting base criteria config:', err)
      showToast('Failed to reset base criteria', 'error')
    }
  }

  const getOverrideCount = (campaignName) => {
    const found = campaignsWithOverrides.find((c) => c.campaignName === campaignName)
    return found ? found.overrideCount : 0
  }

  const handleTestPromptRules = async (criterionKey) => {
    const callId = (promptTestCallIds[criterionKey] || '').trim()
    if (!callId) {
      showToast('Please enter a call ID', 'error')
      return
    }
    const rules = getPromptRules(criterionKey)
    setPromptTestLoading((prev) => ({ ...prev, [criterionKey]: true }))
    setPromptTestResults((prev) => ({ ...prev, [criterionKey]: null }))
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigBaseCriteriaTest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          criterionKey,
          callId,
          matchingRules: rules.length > 0 ? rules.filter((r) => r.trim()) : undefined,
        }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Test failed')
      setPromptTestResults((prev) => ({ ...prev, [criterionKey]: data.data }))
    } catch (err) {
      console.error('Error testing prompt rules:', err)
      setPromptTestResults((prev) => ({
        ...prev,
        [criterionKey]: { result: 'ERROR', reason: err.message },
      }))
    } finally {
      setPromptTestLoading((prev) => ({ ...prev, [criterionKey]: false }))
    }
  }

  const getCriteriaCount = (campaignName) => {
    const found = campaignsWithCriteria.find((c) => c.campaignName === campaignName)
    return found ? found.activeCount : 0
  }

  // Fetch all base criteria (global, not campaign-specific) for prompt rules section
  const fetchAllBaseCriteria = useCallback(async () => {
    setAllBaseCriteriaLoading(true)
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigBaseCriteria)
      const data = await response.json()
      if (!response.ok) {
        showToast(data?.error || 'Failed to fetch base criteria', 'error')
        setAllBaseCriteria([])
        return
      }
      setAllBaseCriteria(Array.isArray(data?.data) ? data.data : [])
    } catch (err) {
      console.error('Error fetching all base criteria:', err)
      showToast('Failed to fetch base criteria', 'error')
      setAllBaseCriteria([])
    } finally {
      setAllBaseCriteriaLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    fetchAllBaseCriteria()
  }, [fetchAllBaseCriteria])

  // Get the current rules for a criterion (local edits take priority)
  const getPromptRules = (criterionKey) => {
    if (promptConfigChanges[criterionKey] !== undefined) {
      return promptConfigChanges[criterionKey]
    }
    const criterion = allBaseCriteria.find((c) => c.criterionKey === criterionKey)
    return criterion?.promptConfig?.matchingRules || []
  }

  const handlePromptRuleChange = (criterionKey, index, value) => {
    const currentRules = [...getPromptRules(criterionKey)]
    currentRules[index] = value
    setPromptConfigChanges((prev) => ({ ...prev, [criterionKey]: currentRules }))
  }

  const handleAddPromptRule = (criterionKey) => {
    const currentRules = [...getPromptRules(criterionKey)]
    currentRules.push('')
    setPromptConfigChanges((prev) => ({ ...prev, [criterionKey]: currentRules }))
  }

  const handleDeletePromptRule = (criterionKey, index) => {
    const currentRules = [...getPromptRules(criterionKey)]
    currentRules.splice(index, 1)
    setPromptConfigChanges((prev) => ({ ...prev, [criterionKey]: currentRules }))
  }

  const handleSavePromptConfig = async (criterionKey) => {
    const rules = getPromptRules(criterionKey)
    const filteredRules = rules.filter((r) => r.trim() !== '')
    setSavingPromptConfig(criterionKey)
    try {
      const response = await authenticatedFetch(apiEndpoints.qaConfigBaseCriterion(criterionKey), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptConfig: { matchingRules: filteredRules },
        }),
      })
      if (!response.ok) throw new Error('Failed to save prompt config')
      showToast('Prompt rules saved successfully')
      setPromptConfigChanges((prev) => {
        const next = { ...prev }
        delete next[criterionKey]
        return next
      })
      fetchAllBaseCriteria()
    } catch (err) {
      console.error('Error saving prompt config:', err)
      showToast('Failed to save prompt rules', 'error')
    } finally {
      setSavingPromptConfig(null)
    }
  }

  return (
    <div className={styles.root}>
      {/* Base Criteria Prompt Rules */}
      <Card className={styles.promptRulesCard}>
        <CardContent style={{ padding: 28 }}>
          <Box className={styles.promptRulesHeader}>
            <Box className={styles.promptRulesIcon}>
              <TuneIcon style={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" className={styles.cardTitle}>
                Base Criteria Prompt Rules
              </Typography>
              <Typography
                variant="body2"
                style={{ color: '#64748B', fontSize: '0.8rem', marginTop: 2 }}
              >
                Configure matching rules used by base criteria AI prompts. Changes apply globally.
              </Typography>
            </Box>
          </Box>

          <Divider style={{ margin: '20px 0 16px', opacity: 0.5 }} />

          {allBaseCriteriaLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} style={{ color: '#2E3C52' }} />
            </Box>
          ) : (
            allBaseCriteria.map((criterion) => {
              const rules = getPromptRules(criterion.criterionKey)
              const hasRules =
                rules.length > 0 || (criterion.promptConfig?.matchingRules || []).length > 0
              const hasChanges = promptConfigChanges[criterion.criterionKey] !== undefined
              const testResult = promptTestResults[criterion.criterionKey]
              const testResultClass = testResult
                ? testResult.result === 'PASS'
                  ? styles.promptTestResultPass
                  : testResult.result === 'FAIL' || testResult.result === 'ERROR'
                    ? styles.promptTestResultFail
                    : styles.promptTestResultNa
                : ''

              return (
                <Accordion key={criterion.criterionKey} className={styles.promptRulesAccordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon style={{ color: '#64748B' }} />}
                    className={styles.promptRulesAccordionSummary}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 10, width: '100%' }}>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 600, color: '#1E293B', flex: 1 }}
                      >
                        {criterion.criterionName}
                      </Typography>
                      {hasRules ? (
                        <Chip
                          size="small"
                          label={`${rules.length} rule${rules.length !== 1 ? 's' : ''}`}
                          className={styles.promptChipRules}
                        />
                      ) : (
                        <Chip size="small" label="No rules" className={styles.promptChipNoRules} />
                      )}
                      {hasChanges && (
                        <Chip size="small" label="Unsaved" className={styles.promptChipUnsaved} />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={styles.promptRulesAccordionDetails}>
                    {rules.length === 0 && !hasChanges ? (
                      <Typography className={styles.promptNoRulesText}>
                        No configurable rules for this criterion. Click &ldquo;Add Rule&rdquo; to
                        create one.
                      </Typography>
                    ) : (
                      rules.map((rule, index) => (
                        <Box key={index} className={styles.promptRuleRow}>
                          <Box className={styles.promptRuleIndex}>{index + 1}</Box>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={rule}
                            onChange={(e) =>
                              handlePromptRuleChange(criterion.criterionKey, index, e.target.value)
                            }
                            placeholder="Describe a matching rule..."
                            className={styles.promptRuleField}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePromptRule(criterion.criterionKey, index)}
                            className={styles.promptRuleDeleteBtn}
                          >
                            <DeleteIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      ))
                    )}

                    <Box className={styles.promptButtonRow}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon style={{ fontSize: 16 }} />}
                        onClick={() => handleAddPromptRule(criterion.criterionKey)}
                        className={styles.promptAddRuleBtn}
                      >
                        Add Rule
                      </Button>
                      {hasChanges && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            savingPromptConfig === criterion.criterionKey ? (
                              <CircularProgress size={14} style={{ color: '#fff' }} />
                            ) : (
                              <SaveIcon style={{ fontSize: 16 }} />
                            )
                          }
                          onClick={() => handleSavePromptConfig(criterion.criterionKey)}
                          disabled={savingPromptConfig === criterion.criterionKey}
                          className={styles.promptSaveBtn}
                        >
                          Save Rules
                        </Button>
                      )}
                    </Box>

                    {/* Test Section */}
                    <Box className={styles.promptTestSection}>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 600, color: '#2E3C52', fontSize: '0.85rem' }}
                      >
                        Test Rules
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ color: '#94A3B8', display: 'block', marginTop: 2 }}
                      >
                        Run this criterion against a real call using current rules. QA results are
                        not stored. If the call has no transcript, one will be generated and saved.
                      </Typography>
                      <Box className={styles.promptTestInputRow}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Enter Call ID..."
                          value={promptTestCallIds[criterion.criterionKey] || ''}
                          onChange={(e) =>
                            setPromptTestCallIds((prev) => ({
                              ...prev,
                              [criterion.criterionKey]: e.target.value,
                            }))
                          }
                          className={styles.promptRuleField}
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            promptTestLoading[criterion.criterionKey] ? (
                              <CircularProgress size={14} />
                            ) : (
                              <PlayIcon style={{ fontSize: 16 }} />
                            )
                          }
                          onClick={() => handleTestPromptRules(criterion.criterionKey)}
                          disabled={
                            promptTestLoading[criterion.criterionKey] ||
                            !(promptTestCallIds[criterion.criterionKey] || '').trim()
                          }
                          className={styles.promptTestBtn}
                        >
                          {promptTestLoading[criterion.criterionKey] ? 'Running...' : 'Test'}
                        </Button>
                      </Box>
                    </Box>

                    {/* Test Result */}
                    {testResult && (
                      <Box className={`${styles.promptTestResult} ${testResultClass}`}>
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 10, marginBottom: 8 }}
                        >
                          <Chip
                            size="small"
                            label={testResult.result}
                            style={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 24,
                              borderRadius: 6,
                              backgroundColor:
                                testResult.result === 'PASS'
                                  ? '#4CAF50'
                                  : testResult.result === 'FAIL'
                                    ? '#EF4444'
                                    : '#6B7280',
                              color: '#fff',
                            }}
                          />
                          {testResult.callerCompany && (
                            <Typography variant="caption" style={{ color: '#64748B' }}>
                              Customer Company: &ldquo;{testResult.callerCompany}&rdquo;
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          style={{ color: '#1E293B', fontSize: '0.85rem', lineHeight: 1.5 }}
                        >
                          {testResult.reason}
                        </Typography>
                        {testResult.evidence && (
                          <Typography
                            variant="caption"
                            style={{
                              color: '#64748B',
                              fontStyle: 'italic',
                              display: 'block',
                              marginTop: 6,
                              padding: '8px 12px',
                              backgroundColor: 'rgba(255,255,255,0.6)',
                              borderRadius: 8,
                              borderLeft: '3px solid #CBD5E1',
                            }}
                          >
                            {testResult.evidence}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )
            })
          )}
        </CardContent>
      </Card>

      {/* Campaign Selection */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Typography variant="h6" className={styles.cardTitle}>
              Select Campaign
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                refetchCampaignsWithCriteria()
                if (selectedCampaign) refetchCriteria()
              }}
              disabled={criteriaLoading}
              className={styles.actionButton}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Refresh
            </Button>
          </Box>

          <AutoCompleteInput
            options={allCampaigns}
            value={selectedCampaign}
            onChange={handleCampaignChange}
            badges={[
              { getCount: (option) => getCriteriaCount(option.name), label: 'custom' },
              {
                getCount: (option) => getOverrideCount(option.name),
                label: 'base overrides',
                color: 'warning',
              },
            ]}
            placeholder="Search campaigns..."
            noOptionsText="No campaigns found"
            className={styles.campaignAutocomplete}
          />

          {/* Campaign Status */}
          {selectedCampaign && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
              <Box display="flex" alignItems="center">
                {criteria.length > 0 ? (
                  <StatusChip
                    label={`${criteria.filter((c) => c.isActive).length} Active Criteria`}
                    color="success"
                  />
                ) : (
                  <StatusChip label="No Custom Criteria" color="warning" variant="soft" />
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={!selectedCampaign}
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                Add Criterion
              </Button>
            </Box>
          )}

          {/* Criteria Table */}
          {selectedCampaign && (
            <div style={{ marginTop: 16 }}>
              <ResponsiveTable
                columns={[
                  {
                    id: 'criterionName',
                    label: 'Criterion Name',
                    renderCell: (row) => (
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {row.criterionName}
                      </Typography>
                    ),
                  },
                  {
                    id: 'criterionType',
                    label: 'Type',
                    renderCell: (row) =>
                      row.criterionType && row.criterionType !== 'custom' ? (
                        <StatusChip
                          size="small"
                          label={evaluationTemplates[row.criterionType]?.name || row.criterionType}
                          color="info"
                          variant="soft"
                        />
                      ) : (
                        <StatusChip size="small" label="Custom" color="primary" variant="soft" />
                      ),
                  },
                  {
                    id: 'isActive',
                    label: 'Status',
                    renderCell: (row) => (
                      <StatusChip
                        size="small"
                        label={row.isActive ? 'Active' : 'Inactive'}
                        color={row.isActive ? 'success' : 'neutral'}
                      />
                    ),
                  },
                  {
                    id: 'createdBy',
                    label: 'Created By',
                    renderCell: (row) => (
                      <Typography variant="body2" color="textSecondary">
                        {row.createdBy}
                      </Typography>
                    ),
                  },
                  {
                    id: 'updatedAt',
                    label: 'Last Updated',
                    renderCell: (row) => (
                      <Typography variant="body2" color="textSecondary">
                        {new Date(row.updatedAt).toLocaleDateString()}
                      </Typography>
                    ),
                  },
                  {
                    id: 'actions',
                    label: 'Actions',
                    align: 'right',
                    renderCell: (row) => (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View History">
                          <IconButton size="small" onClick={() => handleViewHistory(row)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            style={{ color: 'var(--color-error)' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ),
                  },
                ]}
                data={criteria}
                loading={criteriaLoading}
                variant="qa"
                emptyState={
                  <Box className={styles.emptyState}>
                    <AddIcon className={styles.emptyIcon} />
                    <Typography variant="h6" gutterBottom>
                      No Custom Criteria
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Add your first custom QA criterion for this campaign
                    </Typography>
                  </Box>
                }
              />
            </div>
          )}

          {/* Base QA Criteria Section */}
          {selectedCampaign && (
            <>
              <Divider style={{ margin: '24px 0 16px' }} />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginBottom={2}
              >
                <Box>
                  <Typography variant="h6" className={styles.cardTitle}>
                    Base QA Criteria
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configure which base criteria run for this campaign. Unconfigured campaigns use
                    global defaults.
                  </Typography>
                </Box>
                <Box display="flex" style={{ gap: 8 }}>
                  {baseCriteriaConfig.some((c) => c.isOverridden) && (
                    <Button
                      size="small"
                      onClick={() => setResetConfirmDialog(true)}
                      startIcon={<ResetIcon />}
                      className={styles.actionButton}
                      style={{ color: '#DC2626', borderColor: '#DC2626', border: '1px solid' }}
                    >
                      Reset to Defaults
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveBaseCriteria}
                    disabled={!baseCriteriaHasChanges || savingBaseCriteria}
                    startIcon={savingBaseCriteria ? <CircularProgress size={16} /> : <SaveIcon />}
                    className={`${styles.actionButton} ${styles.primaryButton}`}
                  >
                    Save
                  </Button>
                </Box>
              </Box>

              {baseCriteriaLoading ? (
                <LoadingSpinner size={32} />
              ) : (
                <div style={{ marginTop: 16 }}>
                  <ResponsiveTable
                    columns={[
                      {
                        id: 'criterionName',
                        label: 'Criterion',
                        renderCell: (row) => (
                          <>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {row.criterionName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {row.criterionKey}
                            </Typography>
                          </>
                        ),
                      },
                      {
                        id: 'isEnabled',
                        label: 'Enabled',
                        align: 'center',
                        renderCell: (row) => (
                          <Switch
                            checked={row.isEnabled}
                            onChange={() => handleToggleBaseCriterion(row.criterionKey)}
                            color="primary"
                            size="small"
                          />
                        ),
                      },
                      {
                        id: 'dispositions',
                        label: 'Dispositions',
                        cellStyle: { minWidth: 300 },
                        renderCell: (row) => (
                          <>
                            <Box display="flex" style={{ gap: 2, marginBottom: 6 }}>
                              <Box
                                onClick={() =>
                                  handleBaseFilterModeChange(row.criterionKey, 'include')
                                }
                                style={{
                                  padding: '2px 10px',
                                  borderRadius: '10px 0 0 10px',
                                  fontSize: 'var(--font-size-xs)',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  backgroundColor:
                                    (row.dispositionFilterMode || 'include') === 'include'
                                      ? 'var(--color-deep-blue)'
                                      : '#F1F5F9',
                                  color:
                                    (row.dispositionFilterMode || 'include') === 'include'
                                      ? 'var(--color-bg-white)'
                                      : 'var(--color-text-secondary)',
                                  border: '1px solid',
                                  borderColor:
                                    (row.dispositionFilterMode || 'include') === 'include'
                                      ? 'var(--color-deep-blue)'
                                      : 'var(--color-border)',
                                  userSelect: 'none',
                                  lineHeight: '20px',
                                }}
                              >
                                Include
                              </Box>
                              <Box
                                onClick={() =>
                                  handleBaseFilterModeChange(row.criterionKey, 'exclude')
                                }
                                style={{
                                  padding: '2px 10px',
                                  borderRadius: '0 10px 10px 0',
                                  fontSize: 'var(--font-size-xs)',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  backgroundColor:
                                    (row.dispositionFilterMode || 'include') === 'exclude'
                                      ? 'var(--color-deep-blue)'
                                      : '#F1F5F9',
                                  color:
                                    (row.dispositionFilterMode || 'include') === 'exclude'
                                      ? 'var(--color-bg-white)'
                                      : 'var(--color-text-secondary)',
                                  border: '1px solid',
                                  borderColor:
                                    (row.dispositionFilterMode || 'include') === 'exclude'
                                      ? 'var(--color-deep-blue)'
                                      : 'var(--color-border)',
                                  userSelect: 'none',
                                  lineHeight: '20px',
                                }}
                              >
                                Exclude
                              </Box>
                            </Box>
                            <Autocomplete
                              multiple
                              size="small"
                              options={filterOptions.dispositions}
                              value={row.appliesToDispositions || []}
                              onChange={(_, newValue) =>
                                handleBaseDispositionChange(row.criterionKey, newValue)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  placeholder={
                                    row.appliesToDispositions?.length === 0
                                      ? (row.dispositionFilterMode || 'include') === 'exclude'
                                        ? 'None Excluded'
                                        : 'All Dispositions'
                                      : ''
                                  }
                                  size="small"
                                  InputProps={{
                                    ...params.InputProps,
                                    style: { borderRadius: 10 },
                                  }}
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    key={option}
                                    label={option}
                                    size="small"
                                    variant="outlined"
                                    {...getTagProps({ index })}
                                    style={{
                                      borderColor:
                                        (row.dispositionFilterMode || 'include') === 'exclude'
                                          ? '#DC2626'
                                          : 'var(--color-deep-blue)',
                                      color:
                                        (row.dispositionFilterMode || 'include') === 'exclude'
                                          ? '#DC2626'
                                          : 'var(--color-deep-blue)',
                                    }}
                                  />
                                ))
                              }
                            />
                          </>
                        ),
                      },
                      {
                        id: 'status',
                        label: 'Status',
                        align: 'center',
                        renderCell: (row) => (
                          <StatusChip
                            size="small"
                            label={row.isOverridden ? 'Customized' : 'Default'}
                            color={row.isOverridden ? 'info' : 'neutral'}
                            variant="soft"
                          />
                        ),
                      },
                    ]}
                    data={baseCriteriaConfig}
                    loading={false}
                    variant="qa"
                    emptyState={
                      <Typography
                        color="textSecondary"
                        style={{ padding: 32, textAlign: 'center' }}
                      >
                        No base criteria configured. Run the database migration to seed default
                        criteria.
                      </Typography>
                    }
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Campaigns with Criteria Summary */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Typography variant="h6" className={styles.cardTitle} gutterBottom>
            Campaigns with Custom Criteria
          </Typography>
          <Divider style={{ margin: '16px 0' }} />

          {campaignsWithCriteria.length === 0 ? (
            <Box className={styles.emptyState} style={{ padding: 32 }}>
              <Typography variant="body1" color="textSecondary">
                No campaigns have custom criteria defined yet. Select a campaign above to add your
                first criterion.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
              {campaignsWithCriteria.map((campaign) => (
                <Paper
                  key={campaign.campaignName}
                  className={styles.summaryCard}
                  onClick={() =>
                    setSelectedCampaign({
                      name: campaign.campaignName,
                      displayName: campaign.campaignName,
                    })
                  }
                  style={{
                    padding: 20,
                    borderRadius: 12,
                    minWidth: 200,
                    border:
                      selectedCampaign?.name === campaign.campaignName
                        ? '2px solid var(--color-deep-blue)'
                        : '1px solid var(--color-border)',
                  }}
                  elevation={0}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {campaign.campaignName}
                  </Typography>
                  <Box display="flex" alignItems="baseline" style={{ gap: 8 }}>
                    <Typography className={styles.criteriaCount}>{campaign.activeCount}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      / {campaign.totalCount} total
                    </Typography>
                  </Box>
                  <Typography variant="caption" style={{ color: '#4CAF50' }}>
                    active criteria
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <QACriteriaDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editingCriterion={editingCriterion}
        classes={styles}
        formData={formData}
        setFormData={setFormData}
        generatedPrompt={generatedPrompt}
        promptLoading={promptLoading}
        evaluationTemplates={evaluationTemplates}
        allCampaigns={allCampaigns}
        selectedCampaign={selectedCampaign}
        selectedCampaignsForCreate={selectedCampaignsForCreate}
        setSelectedCampaignsForCreate={setSelectedCampaignsForCreate}
        getCriteriaCount={getCriteriaCount}
        toggleCheckItem={toggleCheckItem}
        handleTypeChange={handleTypeChange}
        getCurrentTemplate={getCurrentTemplate}
        insertVariable={insertVariable}
        insertSamplePrompt={insertSamplePrompt}
        testCallId={testCallId}
        setTestCallId={setTestCallId}
        testResult={testResult}
        testLoading={testLoading}
        handleTestPrompt={handleTestPrompt}
        campaignCalls={campaignCalls}
        callsLoading={callsLoading}
        callInputMode={callInputMode}
        setCallInputMode={setCallInputMode}
        manualCallId={manualCallId}
        setManualCallId={setManualCallId}
        callValidation={callValidation}
        setCallValidation={setCallValidation}
        validating={validating}
        verifyCallId={verifyCallId}
        fetchCampaignCalls={fetchCampaignCalls}
        saving={saving}
        onSave={handleSaveCriterion}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ style: { borderRadius: 16, padding: 8 } }}
      >
        <DialogTitle>
          <Typography variant="h6" style={{ color: 'var(--color-error)', fontWeight: 600 }}>
            Delete Criterion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            Are you sure you want to delete &ldquo;{deletingCriterion?.criterionName}&rdquo;?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={saving}
            style={{ backgroundColor: 'var(--color-error)', color: '#fff', borderRadius: 10 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <CriteriaHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        historyData={historyData}
        classes={styles}
      />

      {/* Reset Base Criteria Confirmation Dialog */}
      <Dialog
        open={resetConfirmDialog}
        onClose={handleResetDialogClose}
        PaperProps={{ style: { borderRadius: 16, padding: 8 } }}
      >
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 600, color: 'var(--color-deep-blue)' }}>
            Reset Base Criteria to Defaults
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            This will remove all custom base criteria configuration for
            <strong> {selectedCampaign?.displayName}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All 6 base criteria will revert to global defaults (enabled for all dispositions).
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => setResetConfirmDialog(false)}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResetBaseCriteria}
            style={{ backgroundColor: 'var(--color-error)', color: '#fff', borderRadius: 10 }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </div>
  )
}
